/**
 * resolveIndexLinks - Resolves numeric array indices in links to node IDs
 *
 * Useful when links reference nodes by their array position (index)
 * rather than by ID. Converts to ID-based references for consistency.
 *
 * Note: ForceGraph already handles numeric indices internally, so this
 * utility is mainly for users who want to pre-process data or chain
 * with other transformations.
 *
 * @param {Object} network - { nodes, links } network data
 * @param {Object} options - Configuration options
 * @param {function} [options.nodeId] - Extract ID from node (default: d => d.id)
 * @param {function} [options.linkSource] - Extract source from link (default: d => d.source)
 * @param {function} [options.linkTarget] - Extract target from link (default: d => d.target)
 * @returns {Object} { nodes, links } with resolved references
 *
 * @example
 * const data = {
 *   nodes: [{ id: 'A' }, { id: 'B' }, { id: 'C' }],
 *   links: [{ source: 0, target: 1 }, { source: 1, target: 2 }]
 * };
 * const resolved = resolveIndexLinks(data);
 * // => { nodes: [...], links: [{ source: 'A', target: 'B' }, { source: 'B', target: 'C' }] }
 */
export function resolveIndexLinks(
  network,
  {
    nodeId = (d) => d.id,
    linkSource = (d) => d.source,
    linkTarget = (d) => d.target,
  } = {}
) {
  if (!network || !network.nodes) {
    throw new Error("resolveIndexLinks: network must have a nodes array");
  }

  const nodes = network.nodes;

  const links = (network.links || []).map((link) => {
    const source = linkSource(link);
    const target = linkTarget(link);

    return {
      ...link,
      source: typeof source === "number" ? nodeId(nodes[source]) : source,
      target: typeof target === "number" ? nodeId(nodes[target]) : target,
    };
  });

  return { nodes: [...nodes], links };
}
